import React, { useState, useEffect, useRef, useMemo } from 'react';
import { LinearProgress, Tooltip } from '@material-ui/core';
import { enqueueSnackbar } from 'notistack';
import MaximizeIcon from '@mui/icons-material/OpenInFull';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import cx from 'classnames';

import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import styles from './ComposerDownload.module.scss';
import { findSearchValue, findSearchValueFull, getTokenFromLocalStorage } from '../../../helpers';
import Modal from '../../modules/Modal';
import {
  applyAngleOrderFromFrontend,
  applyClipOrderFromFrontend,
  cancelComposerDownloadStart,
  createComposerDownloadJob,
  createComposerDownloadJobPerClip,
  createComposerDownloadJobPerClipIds,
  estimateComposerDownload,
  getExportFiles,
  getExportSizeEstimate,
  getExportManifest,
  getUserOrganization,
} from '../../../api';
import { ReactComponent as Minimize } from '../../../assets/icons/minimize.svg';
import { ReactComponent as Close } from '../../../assets/icons/close.svg';
import { setDownloadManagerType } from '../../../store/downloadManager/downloadManagerSlice';
import type { DownloadType } from '../../../store/downloadManager/downloadManagerSlice';
import { VIDEOFEED } from '../../../enums';
import { saveMyOrganization } from '../../../store/user/userSlice';

type TDownloadDataProps = {
  isOpen: boolean;
  isVisible: boolean;
  showMinimized: boolean;
  switchModal: () => void;
  jobId: string;
  feedId: string;
  feedTitle: string;
  downloadType?: DownloadType;
  blockedTypes: DownloadType[];
  canAddLayer: boolean;
  tabs: DownloadTab[];
  onAddLayer: () => void;
  onSelectTab: (id: string) => void;
  onMinimizeGroup: () => void;
  onRestoreGroup: () => void;
  stackIndex: number;
};

type DownloadTab = {
  id: string;
  downloadType?: DownloadType;
};

const downloadJson = (filename: string, data: any) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
    a.remove();
  }, 0);
};

type ExportManifest = {
  status: 'queued' | 'processing' | 'ready' | 'error';
  signature: string;
  prefix: string;
  files?: Array<{ key: string; ready?: boolean; progress?: number }>;
  counts?: { total: number; completed: number };
  overallProgress?: number;
  updatedAt?: string;
  error?: string;
  mode?: 'angles' | 'clips' | 'selected_clips';
};

export type Mode = 'angles' | 'anglesPaddingJson' | 'clips' | 'selected' | 'json';

type StandaloneMode = 'angles' | 'anglesPaddingJson';

const downloadTypeLabels: Record<DownloadType, string> = {
  angles: 'Standalone Movie',
  anglesPaddingJson: 'Movie + Padding & JSON',
  clips: 'Individual Clips',
};

const getDownloadTypeLabel = (downloadType?: DownloadType) => (downloadType ? downloadTypeLabels[downloadType] : 'Choose type');

const getDownloadTypeFromMode = (mode: Mode): DownloadType => {
  if (mode === 'anglesPaddingJson') return 'anglesPaddingJson';
  if (mode === 'angles') return 'angles';
  return 'clips';
};

const getModeFromDownloadType = (downloadType?: DownloadType): Mode | null => {
  if (!downloadType) return null;
  return downloadType;
};

const getFirstAvailableMode = (blockedTypes: DownloadType[]): Mode => {
  if (!blockedTypes.includes('angles')) return 'angles';
  if (!blockedTypes.includes('anglesPaddingJson')) return 'anglesPaddingJson';
  return 'clips';
};

type StandaloneReadyState = {
  totalOutputs: number;
  readyOutputs: number;
  isReady: boolean;
  isChecking: boolean;
};

const triggerDownloadViaNewIframe = (url: string, idx: number) => {
  const withHash = `${url}#dl-${idx}-${Date.now()}`;
  const frame = document.createElement('iframe');
  frame.style.display = 'none';
  frame.setAttribute('sandbox', 'allow-downloads allow-scripts allow-same-origin');
  document.body.appendChild(frame);
  frame.src = withHash;

  setTimeout(() => {
    try {
      frame.remove();
    } catch {}
  }, 30000);
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const createDownloadStartRequestId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `download-start-${crypto.randomUUID()}`;
  }
  return `download-start-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const getEstimatedSecondsFromHours = (hours?: number | null) => {
  const safeHours = Number.isFinite(hours) ? (hours as number) : 0;
  return Math.max(0, Math.round(safeHours * 3600));
};

const formatDurationFromHours = (hours?: number | null) => {
  const totalSeconds = getEstimatedSecondsFromHours(hours);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes === 0 && seconds === 0) return '0 minutes';
  if (seconds === 0) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  if (minutes === 0) return `${seconds} second${seconds === 1 ? '' : 's'}`;
  return `${minutes} minute${minutes === 1 ? '' : 's'} ${seconds} second${seconds === 1 ? '' : 's'}`;
};

const formatBytes = (value?: number | null) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let unitIndex = 0;
  let size = bytes;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  const precision = size >= 100 ? 0 : size >= 10 ? 1 : 2;
  return `${size.toFixed(precision)} ${units[unitIndex]}`;
};

const getDisplayFilename = (rawValue?: string) => {
  const raw = String(rawValue || '').trim();
  if (!raw) return '';

  const withoutQueryOrHash = raw.split('?')[0].split('#')[0];
  const normalized = withoutQueryOrHash.endsWith('/') ? withoutQueryOrHash.slice(0, -1) : withoutQueryOrHash;

  const lastSegment = normalized.split('/').pop() || normalized;
  try {
    return decodeURIComponent(lastSegment);
  } catch {
    return lastSegment;
  }
};

const parseServerErrorText = (raw: unknown): string => {
  const text = String(raw || '').trim();
  if (!text) return '';

  try {
    const parsed = JSON.parse(text);
    if (typeof parsed === 'string') return parsed;
    if (typeof parsed?.message === 'string') return parsed.message;
    if (Array.isArray(parsed?.message) && parsed.message.length) {
      return parsed.message.join(', ');
    }
  } catch {
    // keep raw text fallback
  }

  return text;
};

const parseSocketData = (raw: unknown) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw as any;
  if (typeof raw !== 'string' || !raw.trim()) return null;

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const getSocketCorrelation = (msgObj: any, dataObj?: any) => ({
  startRequestId: String(dataObj?.startRequestId || dataObj?.start_request_id || msgObj?.startRequestId || '').trim(),
  transcodeBatchUuid: String(
    dataObj?.transcode_batch_uuid || dataObj?.transcodeBatchUuid || msgObj?.transcode_batch_uuid || msgObj?.transcodeBatchUuid || '',
  ).trim(),
});

const isCancelledRequestError = (err: any) => {
  const name = String(err?.name || '');
  const code = String(err?.code || '');
  const message = String(err?.message || '').toLowerCase();
  return name === 'CanceledError' || code === 'ERR_CANCELED' || message === 'canceled' || message === 'cancelled';
};

const ComposerDownloadModal: React.FC<TDownloadDataProps> = ({
  isOpen,
  isVisible,
  showMinimized,
  switchModal,
  jobId,
  feedId,
  feedTitle,
  downloadType,
  blockedTypes,
  canAddLayer,
  tabs,
  onAddLayer,
  onSelectTab,
  onMinimizeGroup,
  onRestoreGroup,
  stackIndex = 0,
}) => {
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [manifest, setManifest] = useState<ExportManifest | null>(null);
  const [timer, setTimer] = useState<ReturnType<typeof setInterval> | null>(null);
  const [reuseConfirmOpen, setReuseConfirmOpen] = useState(false);
  const transcodeBatchUuidRef = useRef<string | null>(null);
  const { currentSelectedComposerClips, currentComposerData, composers, composerVideoClips, anglesCount } = useAppSelector(
    (state) => state.composersStore,
  );

  const buildVideoFpsList = async (currentComposerData: VIDEOFEED) => {
    const out: any = [];
    const seen = new Set();
    const cache = new Map<string, Promise<number>>(); // url -> Promise<number>

    const DEFAULT_FPS = 25;

    const clampToCommonFps = (v: number): number => {
      // Snap weird values (31.7 etc) to closest common FPS
      const COMMON = [23.976, 24, 25, 29.97, 30, 50, 59.94, 60];
      let best = v;
      let bestDiff = Infinity;

      for (const c of COMMON) {
        const diff = Math.abs(v - c);
        if (diff < 0.3 && diff < bestDiff) {
          best = c;
          bestDiff = diff;
        }
      }
      return best;
    };

    const pickBestUrl = (v: any) => {
      const s3 = v?.s3_keys;
      if (!s3 || typeof s3 !== 'object') return null;

      let bestUrl: string | null = null;
      let bestN = -1;

      for (const [k, val] of Object.entries(s3)) {
        const url = typeof val === 'string' ? val.trim() : '';
        if (!url) continue;

        const n = Number(k);
        if (Number.isFinite(n)) {
          if (n > bestN) {
            bestN = n;
            bestUrl = url;
          }
        } else if (!bestUrl) {
          // fallback: first non-empty if key is not numeric
          bestUrl = url;
        }
      }

      return bestUrl;
    };

    const parseFpsValue = (raw: string): number | null => {
      const s = String(raw || '').trim();
      if (!s) return null;

      // support "30000/1001"
      const frac = s.match(/^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/);
      if (frac) {
        const num = Number(frac[1]);
        const den = Number(frac[2]);
        if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
        const v = num / den;
        if (!Number.isFinite(v) || v <= 0) return null;
        return clampToCommonFps(v);
      }

      // normal decimal
      const v = Number(s);
      if (!Number.isFinite(v) || v <= 0) return null;
      return clampToCommonFps(v);
    };

    // NEW: detect FPS from #EXTINF segment durations in an HLS media playlist
    const detectFpsFromExtinf = (txt: string): number | null => {
      const extinfMatches = [...txt.matchAll(/#EXTINF:([\d.]+)\s*,/g)];
      if (!extinfMatches.length) return null;

      const durations = extinfMatches.map((m) => parseFloat(m[1])).filter((n) => Number.isFinite(n) && n > 0);

      if (!durations.length) return null;

      // Use first N segments as sample
      const sample = durations.slice(0, 400);

      const candidates = [23.976, 24, 25, 29.97, 30, 50, 59.94, 60];

      let bestFps: number | null = null;
      let bestError = Infinity;

      for (const fps of candidates) {
        let maxErr = 0;

        for (const d of sample) {
          const frames = Math.round(d * fps);
          if (frames <= 0) continue;

          const reconstructed = frames / fps;
          const err = Math.abs(d - reconstructed);

          if (err > maxErr) {
            maxErr = err;
            // early break if already worse than best
            if (maxErr > bestError) break;
          }
        }

        if (maxErr < bestError) {
          bestError = maxErr;
          bestFps = fps;
        }
      }

      // strict-ish threshold: if we can't fit well, better return null
      // 0.001s per segment is quite tight.
      if (bestFps != null && bestError < 0.001) {
        return clampToCommonFps(bestFps);
      }

      return null;
    };

    const measureFpsFromVideoElement = (url: string, fallback: number | null): Promise<number> => {
      if (typeof document === 'undefined' || typeof window === 'undefined') {
        return Promise.resolve(typeof fallback === 'number' && fallback > 0 ? fallback : DEFAULT_FPS);
      }

      const normalizedFallback = typeof fallback === 'number' && fallback > 0 ? fallback : DEFAULT_FPS;

      return new Promise((resolve) => {
        const video = document.createElement('video');
        let done = false;

        const finish = (value: number | null) => {
          if (done) return;
          done = true;
          try {
            video.pause();
            video.removeAttribute('src');
            video.load();
            video.remove();
          } catch {}
          let v = value ?? normalizedFallback;
          if (!Number.isFinite(v) || v <= 0) v = normalizedFallback;
          resolve(clampToCommonFps(v));
        };

        const timeoutId = window.setTimeout(() => {
          finish(null);
        }, 4000);

        video.muted = true;
        (video as any).playsInline = true;
        video.preload = 'auto';
        video.src = url;

        video.addEventListener(
          'loadedmetadata',
          () => {
            try {
              if (Number.isFinite(video.duration) && video.duration > 0) {
                video.currentTime = Math.min(1, video.duration * 0.1);
              }
            } catch {}

            const startProbing = () => {
              const hasRVFC = typeof (video as any).requestVideoFrameCallback === 'function';
              if (!hasRVFC) {
                finish(null);
                return;
              }

              let firstTime: number | null = null;
              let lastTime: number | null = null;
              let frames = 0;

              const cb = (_: any, data: { mediaTime: number }) => {
                if (done) return;
                const t = data.mediaTime;
                if (firstTime === null) {
                  firstTime = t;
                } else {
                  frames += 1;
                  lastTime = t;
                  if (frames >= 20 && lastTime! > firstTime + 0.3) {
                    const dt = lastTime! - firstTime;
                    window.clearTimeout(timeoutId);
                    if (dt > 0) {
                      const rawFps = frames / dt;
                      finish(rawFps);
                      return;
                    }
                  }
                }
                (video as any).requestVideoFrameCallback(cb);
              };

              (video as any).requestVideoFrameCallback(cb);
            };

            const playPromise = video.play();
            if (playPromise && typeof playPromise.then === 'function') {
              playPromise.then(startProbing).catch(() => finish(null));
            } else {
              startProbing();
            }
          },
          { once: true },
        );

        video.addEventListener(
          'error',
          () => {
            window.clearTimeout(timeoutId);
            finish(null);
          },
          { once: true },
        );
      });
    };

    const getFps = async (url: string, fallbackFps?: any): Promise<number> => {
      // Normalize fallback: use DB fps if valid, otherwise DEFAULT_FPS
      const normalizedFallback = (() => {
        const n = Number(fallbackFps);
        if (Number.isFinite(n) && n > 0) return clampToCommonFps(n);
        return DEFAULT_FPS;
      })();

      if (!url) return normalizedFallback;

      if (!cache.has(url)) {
        const promise = fetch(url, { cache: 'no-store' })
          .then((r) => (r.ok ? r.text() : ''))
          .then(async (txt) => {
            // Not a playlist / no EXTM3U: we can’t read FRAME-RATE – probe via <video>
            if (!txt || txt.indexOf('#EXTM3U') === -1) {
              const measured = await measureFpsFromVideoElement(url, normalizedFallback);
              return measured;
            }

            // Prefer explicit FRAME-RATE from master playlist if present
            const m = txt.match(/FRAME-RATE\s*=\s*([0-9.]+|[0-9]+\s*\/\s*[0-9]+)/i);
            if (m) {
              const parsed = parseFpsValue(m[1]);
              if (parsed && parsed > 0) {
                return parsed;
              }
            }

            // No FRAME-RATE or unparseable → try to detect from #EXTINF durations
            const detected = detectFpsFromExtinf(txt);
            if (detected && detected > 0) {
              return detected;
            }

            // As a last fallback, probe via <video>
            const measured = await measureFpsFromVideoElement(url, normalizedFallback);
            return measured;
          })
          .catch(() => normalizedFallback)
          .then((v) => {
            let n = Number(v);
            if (!Number.isFinite(n) || n <= 0) n = normalizedFallback;
            return clampToCommonFps(n);
          });

        cache.set(url, promise);
      }

      return cache.get(url)!;
    };

    const pushVideo = async (v: any) => {
      const id = v?.id;
      if (!id || seen.has(id)) return;
      seen.add(id);
      const url = pickBestUrl(v);
      if (!url) return;

      const fpsRaw = await getFps(url, v?.fps);
      const fps = Number.isFinite(fpsRaw) && fpsRaw > 0 ? fpsRaw : DEFAULT_FPS;

      console.log(['fpsForVideo', id, fps, 'url', url]);
      out.push({ videoId: id, fps });
    };

    const baseVideos = Array.isArray(currentComposerData?.videos) ? currentComposerData.videos : [];

    for (const v of baseVideos) {
      // eslint-disable-next-line no-await-in-loop
      await pushVideo(v);
    }

    const topics = Array.isArray(currentComposerData?.topics) ? currentComposerData.topics : [];

    for (const t of topics) {
      const clips = Array.isArray(t?.clips) ? t.clips : [];
      for (const c of clips) {
        const vids = Array.isArray(c?.videos) ? c.videos : [];
        for (const v of vids) {
          // eslint-disable-next-line no-await-in-loop
          await pushVideo(v);
        }
      }
    }

    return out;
  };

  const { currentVideoThatInFrame, socket } = useAppSelector((state) => state.sharedStore);

  const [downloading, setDownloading] = useState(false);
  const [dlTotal, setDlTotal] = useState(0);
  const [dlIndex, setDlIndex] = useState(0);

  const initialMode = getModeFromDownloadType(downloadType) || getFirstAvailableMode(blockedTypes);
  const [mode, setMode] = useState<Mode>(initialMode);
  const isZipMode = mode === 'clips' || mode === 'selected';

  const [selectAllClips, setSelectAllClips] = useState(true);
  const [selectAllAngles, setSelectAllAngles] = useState(true);

  const [formatForAnglesImport, setFormatForAnglesImport] = useState(true);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [hoursLimitMsg, setHoursLimitMsg] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<{
    hours: number;
    totalOutputs: number;
    newOutputs: number;
    reusedOutputs: number;
  } | null>(null);
  const [estimatedZipBytes, setEstimatedZipBytes] = useState<number | null>(null);
  const [estimatedZipBytesLoading, setEstimatedZipBytesLoading] = useState(false);

  const autoDownloadedForPrefixRef = useRef<Record<string, boolean>>({});
  const autoTriggeredRef = useRef(false);
  const startRequestAbortControllerRef = useRef<AbortController | null>(null);
  const startRequestIdRef = useRef<string | null>(null);
  const cancelRequestedRef = useRef(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [standaloneReady, setStandaloneReady] = useState<Record<StandaloneMode, StandaloneReadyState>>({
    angles: {
      totalOutputs: 0,
      readyOutputs: 0,
      isReady: false,
      isChecking: false,
    },
    anglesPaddingJson: {
      totalOutputs: 0,
      readyOutputs: 0,
      isReady: false,
      isChecking: false,
    },
  });

  const showExportError = (err: any, fallbackMessage: string) => {
    const msg = err?.response?.data?.message || err?.message || fallbackMessage;
    const isLimit = msg === 'Download limit reached' || msg === 'Hours limit period ended' || /hours limit/i.test(msg);
    if (isLimit) {
      setExportError(msg);
      setStarted(false);
      setManifest(null);
      setPolling(false);
      setDownloading(false);
      if (timer) {
        clearInterval(timer);
        setTimer(null);
      }
    }
    enqueueSnackbar(msg, { variant: isLimit ? 'warning' : 'error' });
  };

  const throwIfStartRequestCancelled = (signal?: AbortSignal) => {
    if (!signal?.aborted) return;
    const canceledError = new Error('canceled');
    (canceledError as any).name = 'CanceledError';
    throw canceledError;
  };

  const clearStartRequestAbortController = (controller?: AbortController) => {
    if (!controller || startRequestAbortControllerRef.current === controller) {
      startRequestAbortControllerRef.current = null;
    }
  };

  const handleCancelBeforeTranscoding = () => {
    const controller = startRequestAbortControllerRef.current;
    if (!controller || controller.signal.aborted) return;
    cancelRequestedRef.current = true;
    const currentStartRequestId = startRequestIdRef.current;
    if (currentStartRequestId) {
      void cancelComposerDownloadStart(currentStartRequestId).catch(() => {});
    }

    controller.abort();
    clearStartRequestAbortController(controller);
    startRequestIdRef.current = null;
    onClose();
    enqueueSnackbar(`Export canceled for "${feedTitle}"`, { variant: 'info' });
  };

  const markDownloadFailed = (message?: string, variant: 'warning' | 'error' = 'warning') => {
    const msg = String(message || `Download failed for "${feedTitle}"`).trim() || `Download failed for "${feedTitle}"`;
    setExportError(msg);
    setStarted(false);
    setPolling(false);
    setDownloading(false);
    setZipFinalizing(false);
    setManifest((prev) => ({
      status: 'error',
      signature: prev?.signature || '',
      prefix: prev?.prefix || '',
      overallProgress: Number(prev?.overallProgress ?? 0),
      files: prev?.files,
      counts: prev?.counts,
      updatedAt: new Date().toISOString(),
      error: msg,
      mode: prev?.mode,
    }));
    if (timer) {
      clearInterval(timer);
      setTimer(null);
    }
    enqueueSnackbar(msg, { variant });
  };

  // Check topics for duplicate clipIndex and, if found, tell backend to reassign indexes
  const buildApplyOrderPayloadIfNeeded = () => {
    const topics = currentComposerData?.topics || [];
    if (!topics.length) return null;

    let hasDuplicates = false;

    for (const t of topics) {
      const clips = t?.clips || [];
      if (!clips.length) continue;

      const seen = new Map<number, number>();
      for (const c of clips) {
        const idx = Number(c?.clipIndex ?? 0);
        const prev = seen.get(idx) || 0;
        seen.set(idx, prev + 1);
        if (prev + 1 > 1) {
          hasDuplicates = true;
          break;
        }
      }
      if (hasDuplicates) break;
    }

    if (!hasDuplicates) return null;

    return {
      topics: (currentComposerData?.topics || []).map((t: any) => ({
        topicId: Number(t.id),
        clipIds: (t.clips || []).map((c: any) => Number(c.id)).filter((id: number) => Number.isFinite(id)),
      })),
    };
  };

  // Check angles in base videos + per-clip videos and always send FE order to backend
  const buildApplyAngleOrderPayloadIfNeeded = () => {
    const composer = currentComposerData;
    if (!composer) return null;

    // Try to find same composer as in VideoMyComposerList
    const composerIdNum = Number((composer as any).id ?? feedId);
    const composerFromList = composers?.find((c: any) => Number(c.id) === composerIdNum);

    // How many angle rows do we have in UI
    const totalAngles =
      typeof anglesCount === 'number' && anglesCount > 0
        ? anglesCount
        : Math.max(
            (composerVideoClips?.length as number) || 0,
            (composerFromList?.videos?.length as number) || 0,
            ((composer as any).videos?.length as number) || 0,
          );

    if (!totalAngles) return null;

    // 1) Canonical base angle order = what user sees in VideoMyComposerList
    const baseVideoOrderIds: number[] = [];

    for (let index = 0; index < totalAngles; index += 1) {
      let vidId: number | null = null;

      // highest priority: composerVideoClips (per-angle list used in VideoMyComposerList)
      if (Array.isArray(composerVideoClips) && composerVideoClips[index]?.id != null) {
        vidId = Number(composerVideoClips[index].id);
      }
      // fallback: composer from list
      else if (composerFromList?.videos?.[index]?.id != null) {
        vidId = Number(composerFromList.videos[index].id);
      }
      // last fallback: currentComposerData.videos
      else if ((composer as any).videos?.[index]?.id != null) {
        vidId = Number((composer as any).videos[index].id);
      }

      if (Number.isFinite(vidId)) {
        baseVideoOrderIds.push(vidId as number);
      }
    }

    const baseVideoIds = baseVideoOrderIds.filter((id) => Number.isFinite(id));

    // If we still have nothing, don't send payload
    if (!baseVideoIds.length && !(composer as any).topics?.length) {
      return null;
    }

    // Map videoId -> canonical angle index (row index from UI)
    const angleIndexByVideoId = new Map<number, number>();
    baseVideoIds.forEach((id, idx) => {
      angleIndexByVideoId.set(id, idx);
    });

    // 2) Per-clip angle order: align each clip.videos with the same canonical angle order
    const clipsPayload: { clipId: number; videoIds: number[] }[] = [];

    for (const t of (composer as any).topics || []) {
      const clips = (t as any).clips || [];
      for (const c of clips) {
        const vids = (c as any).videos || [];
        if (!Array.isArray(vids) || !vids.length) continue;

        const sortedVideoIds = vids
          .map((v: any) => Number(v?.id))
          .filter((id: number) => Number.isFinite(id))
          .sort((a: number, b: number) => {
            const ai = angleIndexByVideoId.get(a);
            const bi = angleIndexByVideoId.get(b);

            // unknown videos go to the end, keep relative order for unknowns
            if (ai == null && bi == null) return 0;
            if (ai == null) return 1;
            if (bi == null) return -1;
            return ai - bi;
          });

        if (!sortedVideoIds.length) continue;

        clipsPayload.push({
          clipId: Number((c as any).id),
          videoIds: sortedVideoIds,
        });
      }
    }

    return {
      baseVideos: baseVideoIds.length ? { videoIds: baseVideoIds } : undefined,
      clips: clipsPayload.length ? clipsPayload : undefined,
    };
  };

  const ensureAngleIndexesUpToDate = async () => {
    const payload = buildApplyAngleOrderPayloadIfNeeded();
    if (!payload) return; // nothing to fix

    try {
      await applyAngleOrderFromFrontend(String(feedId), payload);
    } catch (e) {
      console.error('Failed to apply angle order from frontend', e);
    }
  };

  const ensureClipIndexesUpToDate = async () => {
    const payload = buildApplyOrderPayloadIfNeeded();
    if (!payload) return; // nothing to fix

    try {
      await applyClipOrderFromFrontend(String(feedId), payload);
    } catch (e) {
      console.error('Failed to apply clip order from frontend', e);
    }
  };

  const [zipProgress, setZipProgress] = useState<number | null>(null);
  const [zipFinalizing, setZipFinalizing] = useState(false);
  const [zipFilesDone, setZipFilesDone] = useState<number | null>(null);
  const [zipFilesTotal, setZipFilesTotal] = useState<number | null>(null);
  const zipFilesTotalRef = useRef<number | null>(null);
  const zipCompletionLockedRef = useRef(false);
  const [exportMessage, setExportMessage] = useState<string | null>(null);

  const effectiveAngleId = useMemo(() => {
    if (selectAllAngles) return undefined;

    const clipIdxRaw = Number(findSearchValue('clip'));
    const clipIdx = Number.isFinite(clipIdxRaw) && clipIdxRaw >= 0 ? clipIdxRaw : 0;

    const videoFromCurrentAngle =
      (Array.isArray(composerVideoClips) ? composerVideoClips[clipIdx] : undefined) ||
      (Array.isArray((currentComposerData as any)?.videos) ? (currentComposerData as any).videos[clipIdx] : undefined);

    const resolvedId = Number(videoFromCurrentAngle?.id ?? currentVideoThatInFrame?.id);
    return Number.isFinite(resolvedId) ? resolvedId : undefined;
  }, [selectAllAngles, composerVideoClips, currentComposerData, currentVideoThatInFrame]);
  const feedIdUrl = findSearchValue('feedId');
  const allComposerClips = useMemo(() => {
    const topics = currentComposerData?.topics;
    const safeTopics = Array.isArray(topics) ? topics : [];
    return safeTopics.flatMap((topic: any) => (Array.isArray(topic?.clips) ? topic.clips : []));
  }, [currentComposerData]);
  const hasMoreThanOneClip = useMemo(() => allComposerClips.length > 1, [allComposerClips]);
  const selectedComposerClipsResolved = useMemo(() => {
    const selected = Array.isArray(currentSelectedComposerClips) ? currentSelectedComposerClips : [];
    if (!selected.length) return [];

    const clipById = new Map<number, any>();
    allComposerClips.forEach((clip: any) => {
      const id = Number(clip?.id);
      if (Number.isFinite(id)) clipById.set(id, clip);
    });

    return selected
      .map((clip: any) => {
        const id = Number(clip?.id);
        return Number.isFinite(id) && clipById.has(id) ? clipById.get(id) : clip;
      })
      .filter(Boolean);
  }, [allComposerClips, currentSelectedComposerClips]);
  const clipsForAngleSelector = useMemo(() => {
    if (mode === 'clips' && !selectAllClips) {
      return selectedComposerClipsResolved;
    }
    return allComposerClips;
  }, [allComposerClips, mode, selectAllClips, selectedComposerClipsResolved]);
  const shouldDisableAngleSelector = useMemo(() => {
    if (!clipsForAngleSelector.length) return false;
    return clipsForAngleSelector.every((clip: any) => {
      const clipVideosCount = Array.isArray(clip?.videos) ? clip.videos.length : 0;
      return clipVideosCount <= 1;
    });
  }, [clipsForAngleSelector]);
  const shouldShowAngleSelector = Boolean(feedIdUrl?.length) && !shouldDisableAngleSelector;

  // flag: once user starts export, hide all options above buttons
  const [started, setStarted] = useState(false);

  const isModeBlocked = (targetMode: Mode) => blockedTypes.includes(getDownloadTypeFromMode(targetMode));
  const selectMode = (targetMode: Mode) => {
    if (loading || polling || downloading || isModeBlocked(targetMode)) return;
    setMode(targetMode);
    dispatch(setDownloadManagerType({ id: jobId, downloadType: getDownloadTypeFromMode(targetMode) }));
  };

  const onClose = () => {
    const controller = startRequestAbortControllerRef.current;
    if (controller && !controller.signal.aborted) {
      controller.abort();
    }
    clearStartRequestAbortController(controller || undefined);

    switchModal();
    setConfirmOpen(false);
    setConfirmLoading(false);
    setEstimate(null);
    setEstimatedZipBytes(null);
    setEstimatedZipBytesLoading(false);
    setLoading(false);
    setPolling(false);
    setManifest(null);
    setDownloading(false);
    setSelectAllAngles(true);
    setSelectAllClips(true);
    setDlTotal(0);
    setDlIndex(0);
    setMode('angles');
    setZipProgress(null);
    setZipFinalizing(false);
    setZipFilesDone(null);
    setZipFilesTotal(null);
    zipCompletionLockedRef.current = false;
    setExportMessage(null);
    setExportError(null);
    setStarted(false);
    setFormatForAnglesImport(true); // reset to default
    setStandaloneReady({
      angles: {
        totalOutputs: 0,
        readyOutputs: 0,
        isReady: false,
        isChecking: false,
      },
      anglesPaddingJson: {
        totalOutputs: 0,
        readyOutputs: 0,
        isReady: false,
        isChecking: false,
      },
    });
    autoDownloadedForPrefixRef.current = {};
    autoTriggeredRef.current = false;
    transcodeBatchUuidRef.current = null;
    startRequestIdRef.current = null;
    cancelRequestedRef.current = false;

    if (timer) {
      clearInterval(timer);
      setTimer(null);
    }
  };

  useEffect(() => {
    if (!isOpen || started) return;

    const jobMode = getModeFromDownloadType(downloadType);
    if (jobMode && jobMode !== mode) {
      setMode(jobMode);
      return;
    }

    const modeType = getDownloadTypeFromMode(mode);
    if (blockedTypes.includes(modeType)) {
      const nextMode = getFirstAvailableMode(blockedTypes);
      setMode(nextMode);
      dispatch(setDownloadManagerType({ id: jobId, downloadType: getDownloadTypeFromMode(nextMode) }));
      return;
    }

    if (downloadType !== modeType) {
      dispatch(setDownloadManagerType({ id: jobId, downloadType: modeType }));
    }
  }, [isOpen, started, downloadType, blockedTypes, mode, dispatch, jobId]);

  const autoDownloadAll = async (prefix: string) => {
    try {
      setDownloading(true);
      const { data } = await getExportFiles(prefix);
      const files: Array<{ key: string; name: string; signedUrl: string }> = data?.files || [];

      const uniqueMap = new Map<string, { key: string; name: string; signedUrl: string }>();
      files.forEach((f) => {
        const uniqKey = f.key || f.signedUrl || f.name;
        if (!uniqueMap.has(uniqKey)) {
          uniqueMap.set(uniqKey, f);
        }
      });
      const uniqueFiles = Array.from(uniqueMap.values());

      if (!uniqueFiles.length) {
        enqueueSnackbar(`Nothing to download for "${feedTitle}"`, { variant: 'warning' });
        return;
      }
      setDlTotal(uniqueFiles.length);
      setDlIndex(0);

      for (let i = 0; i < uniqueFiles.length; i++) {
        const f = uniqueFiles[i];
        setDlIndex(i + 1);
        try {
          triggerDownloadViaNewIframe(f.signedUrl, i);
          // eslint-disable-next-line no-await-in-loop
          await sleep(300);
        } catch {
          enqueueSnackbar(`Failed to start download for ${f.name || f.key} in "${feedTitle}"`, {
            variant: 'error',
          });
        }
      }

      // mark as ready in UI after auto-download
      setManifest({
        status: 'ready',
        signature: '',
        prefix,
        overallProgress: 100,
        files: uniqueFiles.map((f) => ({
          key: f.key || f.name || 'file',
          ready: true,
          progress: 100,
        })),
        counts: { total: uniqueFiles.length, completed: uniqueFiles.length },
      });

      enqueueSnackbar(`Downloads started for "${feedTitle}"`, { variant: 'info' });
    } finally {
      setDlTotal(0);
      setDlIndex(0);
      setDownloading(false);
      fetchCurrentOrg();
    }
  };

  const fetchCurrentOrg = async () => {
    try {
      const { data } = await getUserOrganization(true);
      dispatch(saveMyOrganization(data));
    } catch (err) {
      console.error(err);
    }
  };

  const downloadClipsZip = async (prefixFromManifest?: string | null, formatForAngle?: string, transcodeBatchUuid?: string | null) => {
    try {
      if (!feedId) return;

      setDownloading(true);
      setZipProgress(null); // reset zip % for new run
      setZipFinalizing(false);
      setZipFilesDone(null);
      setZipFilesTotal(null);
      zipFilesTotalRef.current = null;
      zipCompletionLockedRef.current = false;
      const token = getTokenFromLocalStorage();
      if (!token) {
        markDownloadFailed('Unauthorized', 'warning');
        return;
      }

      if (transcodeBatchUuid !== undefined) {
        transcodeBatchUuidRef.current = transcodeBatchUuid || null;
      }

      const hasTranscodeBatchUuid = Boolean(transcodeBatchUuidRef.current);
      const shouldEnableFormatAnglesImport =
        hasTranscodeBatchUuid &&
        (formatForAngle === 'true' ||
          (formatForAngle !== 'false' && formatForAnglesImport));

      fetchCurrentOrg();

      const tokenEndpoint = `${process.env.REACT_APP_API_URL}reflex-composer/composer/${feedId}/clips/zip-token`;
      const tokenRes = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prefix: prefixFromManifest || undefined,
          formatAnglesImport: shouldEnableFormatAnglesImport,
          transcode_batch_uuid: hasTranscodeBatchUuid
            ? transcodeBatchUuidRef.current
            : undefined,
        }),
      });

      if (!tokenRes.ok) {
        let msg = `Failed to start ZIP download for "${feedTitle}"`;
        try {
          const text = await tokenRes.text();
          const parsedMessage = parseServerErrorText(text);
          if (parsedMessage) msg = parsedMessage;
        } catch {
          // ignore
        }
        markDownloadFailed(msg, 'warning');
        return;
      }

      let downloadToken = '';
      try {
        const payload = await tokenRes.json();
        if (typeof payload?.token === 'string') {
          downloadToken = payload.token.trim();
        }
      } catch {
        // ignore
      }

      if (!downloadToken) {
        markDownloadFailed(`Failed to prepare ZIP download for "${feedTitle}"`, 'warning');
        return;
      }

      const directUrl = new URL(
        `${process.env.REACT_APP_API_URL}reflex-composer/composer/${feedId}/clips/zip-token-download`,
      );
      directUrl.searchParams.set('token', downloadToken);
      triggerDownloadViaNewIframe(directUrl.toString(), 0);
      zipCompletionLockedRef.current = true;
      // Mark final file count only once browser download is actually triggered.
      const totalFilesForThisRun = Number(zipFilesTotalRef.current);
      if (Number.isFinite(totalFilesForThisRun) && totalFilesForThisRun > 0) {
        setZipFilesDone(Math.round(totalFilesForThisRun));
      }
      // 100% only after full ZIP payload is received and download trigger happened.
      setZipFinalizing(false);
      setZipProgress(100);

      // mark as ready for ZIP download (notification path)
      setManifest({
        status: 'ready',
        signature: '',
        prefix: prefixFromManifest || '',
        overallProgress: 100,
      });
    } catch (e: any) {
      setZipFinalizing(false);
      markDownloadFailed(parseServerErrorText(e?.message) || `Failed to start ZIP download for "${feedTitle}"`, 'warning');
    } finally {
      setDownloading(false);
    }
  };

  const startPolling = (prefix: string, modeForDownload: Mode) => {
    setPolling(true);
    if (timer) {
      clearInterval(timer);
      setTimer(null);
    }

    const id = setInterval(async () => {
      try {
        const res = await getExportManifest(prefix, transcodeBatchUuidRef.current);
        const data = res?.data ?? res;
        setManifest(data);
        if (data?.status === 'processing' || data?.status === 'ready' || data?.status === 'error') {
          setExportMessage(null);
        }

        if (data?.status === 'error') {
          clearInterval(id);
          setTimer(null);
          setPolling(false);
          markDownloadFailed(data?.error || `Download failed for "${feedTitle}"`, 'warning');
          return;
        }

        const isReady = data?.status === 'ready' || Number(data?.overallProgress ?? 0) >= 100;

        if (isReady) {
          clearInterval(id);
          setTimer(null);
          setPolling(false);

          if (!autoDownloadedForPrefixRef.current[prefix]) {
            autoDownloadedForPrefixRef.current[prefix] = true;
            enqueueSnackbar(`Download content ready for "${feedTitle}"`, {
              variant: 'success',
            });

            if (modeForDownload === 'angles' || modeForDownload === 'anglesPaddingJson') {
              await autoDownloadAll(prefix);
            } else if (modeForDownload === 'clips') {
              await downloadClipsZip(prefix, undefined, transcodeBatchUuidRef.current);
            }
          }
        }
      } catch (err) {
        console.log(err);
      }
    }, 4000);

    setTimer(id);
  };

  const downloadClips = async (forceSelected?: boolean) => {
    setManifest(null);
    setExportMessage(null);
    transcodeBatchUuidRef.current = null;
    cancelRequestedRef.current = false;
    const startRequestId = createDownloadStartRequestId();
    startRequestIdRef.current = startRequestId;
    const selectedClipIds = (currentSelectedComposerClips || []).map((c: any) => Number(c?.id)).filter((n: number) => Number.isFinite(n));

    const clipIds = forceSelected ? selectedClipIds : !selectAllClips ? selectedClipIds : [];
    const startRequestAbortController = new AbortController();
    startRequestAbortControllerRef.current = startRequestAbortController;

    try {
      setExportError(null);
      setStarted(true);
      setLoading(true);

      await ensureAngleIndexesUpToDate();
      throwIfStartRequestCancelled(startRequestAbortController.signal);
      await ensureClipIndexesUpToDate();
      throwIfStartRequestCancelled(startRequestAbortController.signal);

      const payload: any = {
        angleId: effectiveAngleId,
        formatForAnglesImport,
        startRequestId,
      };

      let out: any;
      if (clipIds.length) {
        out = await createComposerDownloadJobPerClipIds(String(feedId), clipIds, payload, startRequestAbortController.signal);
      } else {
        out = await createComposerDownloadJobPerClip(String(feedId), payload, startRequestAbortController.signal);
      }
      if (out?.transcode_batch_uuid) {
        transcodeBatchUuidRef.current = out.transcode_batch_uuid;
      }
      if (out?.prefix) startPolling(out.prefix, 'clips');
      enqueueSnackbar(
        clipIds.length
          ? `Download export started for ${clipIds.length} selected clip(s) in "${feedTitle}"`
          : `Download export started for "${feedTitle}"`,
        { variant: 'success' },
      );
    } catch (e: any) {
      if (isCancelledRequestError(e) || startRequestAbortController.signal.aborted) {
        return;
      }
      const cancelReason = String(e?.response?.data?.message || e?.message || '');
      if (/export canceled before transcoding started/i.test(cancelReason) || cancelRequestedRef.current) {
        return;
      }
      showExportError(e, `Failed to start export for "${feedTitle}"`);
    } finally {
      clearStartRequestAbortController(startRequestAbortController);
      startRequestIdRef.current = null;
      setLoading(false);
    }
  };

  const downloadAngles = async (standaloneMode: StandaloneMode = mode as StandaloneMode) => {
    setManifest(null);
    setExportMessage(null);
    transcodeBatchUuidRef.current = null;
    cancelRequestedRef.current = false;
    const startRequestId = createDownloadStartRequestId();
    startRequestIdRef.current = startRequestId;
    const startRequestAbortController = new AbortController();
    startRequestAbortControllerRef.current = startRequestAbortController;

    try {
      setLoading(true);
      setStarted(true);
      setExportError(null);

      await ensureAngleIndexesUpToDate();
      throwIfStartRequestCancelled(startRequestAbortController.signal);
      await ensureClipIndexesUpToDate();
      throwIfStartRequestCancelled(startRequestAbortController.signal);

      const withJson = standaloneMode === 'anglesPaddingJson';

      const fpsList = await buildVideoFpsList(currentComposerData!);
      throwIfStartRequestCancelled(startRequestAbortController.signal);

      const out = await createComposerDownloadJob(
        String(feedId),
        {
          angleId: effectiveAngleId,
          // Standalone Movie always uses ALL clips now
          withJson,
          startRequestId,
        },
        fpsList,
        startRequestAbortController.signal,
      );

      if (withJson && out?.json) {
        downloadJson(`${feedTitle}.json`, { ...out.json });
      }

      if (out?.transcode_batch_uuid) {
        transcodeBatchUuidRef.current = out.transcode_batch_uuid;
      }
      if (out?.prefix) startPolling(out.prefix, standaloneMode);
      enqueueSnackbar(`Download export started for "${feedTitle}"`, { variant: 'success' });
    } catch (e: any) {
      if (isCancelledRequestError(e) || startRequestAbortController.signal.aborted) {
        return;
      }
      const cancelReason = String(e?.response?.data?.message || e?.message || '');
      if (/export canceled before transcoding started/i.test(cancelReason) || cancelRequestedRef.current) {
        return;
      }
      showExportError(e, `Failed to start export for "${feedTitle}"`);
    } finally {
      clearStartRequestAbortController(startRequestAbortController);
      startRequestIdRef.current = null;
      setLoading(false);
    }
  };

  const hasSelection = Array.isArray(currentSelectedComposerClips) && currentSelectedComposerClips.length > 0;

  const start = async () => {
    // mark that we've started — hide all options
    setStarted(true);

    if (mode === 'angles' || mode === 'anglesPaddingJson') {
      await downloadAngles(mode);
      return;
    }
    if (mode === 'clips') return downloadClips();
    if (mode === 'selected') return downloadClips(true);
  };

  const checkStandaloneReady = async (standaloneMode: StandaloneMode): Promise<StandaloneReadyState> => {
    const res = await estimateComposerDownload(String(feedId), {
      mode: standaloneMode,
      angleId: effectiveAngleId,
    });

    const est = res?.data ?? res;
    const total = Number(est?.totalOutputs ?? 0);
    const reused = Number(est?.reusedOutputs ?? 0);
    const totalOutputs = Number.isFinite(total) && total > 0 ? total : 0;
    const rawReadyOutputs = Number.isFinite(reused) && reused > 0 ? reused : 0;
    const readyOutputs = totalOutputs > 0 ? Math.min(totalOutputs, rawReadyOutputs) : 0;

    return {
      totalOutputs,
      readyOutputs,
      // Strict readiness for selected scope:
      // - Current Angle selected: 1/1 must be ready
      // - All Angles selected: N/N must be ready
      isReady: totalOutputs > 0 && readyOutputs === totalOutputs,
      isChecking: false,
    };
  };

  const startReadyStandaloneDownload = async (standaloneMode: StandaloneMode) => {
    if (loading || polling || downloading || confirmLoading) return;
    if (isModeBlocked(standaloneMode)) return;
    setMode(standaloneMode);
    dispatch(setDownloadManagerType({ id: jobId, downloadType: getDownloadTypeFromMode(standaloneMode) }));
    setConfirmOpen(false);
    setReuseConfirmOpen(false);
    await downloadAngles(standaloneMode);
  };

  const confirmStart = async () => {
    if (loading || polling || downloading || confirmLoading) return;
    if (isModeBlocked(mode)) return;
    if (autoTriggeredRef.current) {
      await start();
      return;
    }
    setConfirmLoading(true);
    try {
      const selectedClipIds = (currentSelectedComposerClips || []).map((c: any) => Number(c?.id)).filter((n: number) => Number.isFinite(n));
      const clipIds = mode === 'selected' ? selectedClipIds : mode === 'clips' && !selectAllClips ? selectedClipIds : [];

      const res = await estimateComposerDownload(String(feedId), {
        mode,
        clipIds,
        angleId: effectiveAngleId,
      });

      const est = res?.data ?? res;
      const total = Number(est?.totalOutputs ?? 0);
      const hours = Number(est?.hours ?? 0);
      const reused = Number(est?.reusedOutputs ?? 0);
      const nextEstimate = {
        hours: Number.isFinite(hours) ? hours : 0,
        totalOutputs: Number.isFinite(total) ? total : 0,
        newOutputs: Number.isFinite(est?.newOutputs) ? Number(est.newOutputs) : Math.max(0, total - reused),
        reusedOutputs: Number.isFinite(reused) ? reused : 0,
      };

      if (!nextEstimate.totalOutputs) {
        enqueueSnackbar(`Nothing to download for "${feedTitle}"`, { variant: 'warning' });
        return;
      }

      setEstimate(nextEstimate);
      const estimatedSeconds = getEstimatedSecondsFromHours(nextEstimate.hours);
      if (estimatedSeconds === 0) {
        setConfirmOpen(false);
        setReuseConfirmOpen(false);
        await start();
      } else {
        setConfirmOpen(true);
      }
    } catch (e: any) {
      let rawMsg = e?.response?.data?.message || '';
      const isHoursLimit = /not enough download hours/i.test(rawMsg);

      if (!rawMsg) {
        rawMsg = 'Failed to estimate download hours';
      }

      enqueueSnackbar(rawMsg, { variant: 'warning' });
      if (isHoursLimit) {
        setHoursLimitMsg(rawMsg);
        setConfirmOpen(false);
        setReuseConfirmOpen(false);
        setStarted(false);
        return;
      }
    } finally {
      setConfirmLoading(false);
    }
  };

  // AUTO: notification provides ?mode=...&prefix=... and implies READY => no polling
  useEffect(() => {
    if (!isOpen) return;
    if (autoTriggeredRef.current) return;
    if (!feedTitle) return;

    const urlModeRaw = (findSearchValue('mode') || '').toString();
    const urlPrefix = (findSearchValueFull('prefix') || '').toString();
    const urlFormatForAnglesImport = (findSearchValue('formatForAnglesImport') || '').toString();
    const urlTranscodeBatchUuid = (findSearchValue('transcode_batch_uuid') || '').toString();

    if (!urlModeRaw && !urlPrefix) return;

    // flip state based on query
    const nextFormat = urlFormatForAnglesImport === 'true' || urlFormatForAnglesImport === '1';
    setFormatForAnglesImport(nextFormat);

    // remove from URL so it doesn't re-trigger
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('mode');
      u.searchParams.delete('prefix');
      u.searchParams.delete('formatForAnglesImport');
      u.searchParams.delete('transcode_batch_uuid');
      window.history.replaceState({}, '', `${u.pathname}${u.search}${u.hash}`);
    } catch {}

    const urlMode: Mode =
      urlModeRaw === 'angles' || urlModeRaw === 'anglesPaddingJson' || urlModeRaw === 'clips' || urlModeRaw === 'selected'
        ? (urlModeRaw as Mode)
        : 'angles';

    autoTriggeredRef.current = true;
    setMode(urlMode);
    dispatch(setDownloadManagerType({ id: jobId, downloadType: getDownloadTypeFromMode(urlMode) }));
    setStarted(true);

    if (urlPrefix) {
      if (urlMode === 'angles' || urlMode === 'anglesPaddingJson') {
        autoDownloadAll(urlPrefix);
        return;
      }
      // clips or selected => zip
      downloadClipsZip(urlPrefix, !urlFormatForAnglesImport ? 'false' : 'true', urlTranscodeBatchUuid || null);
      return;
    }
  }, [isOpen, feedTitle]);

  useEffect(() => {
    const isZipMode = mode === 'clips' || mode === 'selected';
    const prefix = String(manifest?.prefix || '').trim();

    if (!isOpen || !isZipMode) {
      setEstimatedZipBytes(null);
      setEstimatedZipBytesLoading(false);
      return;
    }

    if (!prefix || manifest?.status !== 'ready') {
      setEstimatedZipBytes(null);
      setEstimatedZipBytesLoading(false);
      return;
    }

    let cancelled = false;
    setEstimatedZipBytesLoading(true);

    getExportSizeEstimate(prefix)
      .then((res: any) => {
        if (cancelled) return;
        const data = res?.data ?? res;
        const estimated = Number(data?.estimatedZipBytes ?? data?.totalInputBytes ?? 0);
        if (Number.isFinite(estimated) && estimated > 0) {
          setEstimatedZipBytes(estimated);
        } else {
          setEstimatedZipBytes(null);
        }
      })
      .catch(() => {
        if (!cancelled) setEstimatedZipBytes(null);
      })
      .finally(() => {
        if (!cancelled) setEstimatedZipBytesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, mode, manifest?.prefix, manifest?.status]);

  useEffect(() => {
    if (!isOpen || !feedId || started) return;

    let cancelled = false;

    setStandaloneReady((prev) => ({
      angles: { ...prev.angles, isChecking: true },
      anglesPaddingJson: { ...prev.anglesPaddingJson, isChecking: true },
    }));

    (async () => {
      try {
        const [anglesReady, anglesPaddingReady] = await Promise.all([
          checkStandaloneReady('angles'),
          checkStandaloneReady('anglesPaddingJson'),
        ]);

        if (cancelled) return;
        setStandaloneReady({
          angles: anglesReady,
          anglesPaddingJson: anglesPaddingReady,
        });
      } catch {
        if (cancelled) return;
        setStandaloneReady((prev) => ({
          angles: { ...prev.angles, isChecking: false },
          anglesPaddingJson: { ...prev.anglesPaddingJson, isChecking: false },
        }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, feedId, effectiveAngleId, started]);

  const clampMiniPos = (x: number, y: number) => {
    if (typeof window === 'undefined') {
      return { x, y };
    }

    const margin = 16;
    const bubbleWidth = 330;
    const bubbleHeight = 130;

    const maxX = Math.max(margin, window.innerWidth - bubbleWidth - margin);
    const maxY = Math.max(margin, window.innerHeight - bubbleHeight - margin);

    return {
      x: Math.min(Math.max(margin, x), maxX),
      y: Math.min(Math.max(margin, y), maxY),
    };
  };

  const [miniPos, setMiniPos] = useState<{ x: number; y: number }>(() => {
    if (typeof window === 'undefined') {
      return { x: 24, y: 24 };
    }

    const margin = 120;
    const bubbleWidth = 330;
    const bubbleHeight = 130;

    const startX = window.innerWidth - bubbleWidth - margin;

    const baseY = window.innerHeight - bubbleHeight - margin;
    const stackSpacing = bubbleHeight + 8;
    const startY = baseY - (stackIndex || 0) * stackSpacing;

    return clampMiniPos(startX, startY);
  });

  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const handleMiniMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setDragging(true);
    setDragOffset({
      x: e.clientX - miniPos.x,
      y: e.clientY - miniPos.y,
    });
  };

  useEffect(() => {
    if (!dragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const x = e.clientX - dragOffset.x;
      const y = e.clientY - dragOffset.y;
      setMiniPos(clampMiniPos(x, y));
    };

    const handleMouseUp = () => {
      setDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, dragOffset]);

  useEffect(() => {
    const handleResize = () => {
      setMiniPos((prev) => clampMiniPos(prev.x, prev.y));
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (shouldDisableAngleSelector && !selectAllAngles) {
      setSelectAllAngles(true);
    }
  }, [selectAllAngles, shouldDisableAngleSelector]);

  // === EXPORT PREP SOCKET LISTENER ===
  useEffect(() => {
    if (!socket || !isOpen) return;

    const handler = (msg: string) => {
      try {
        const msgObj = JSON.parse(msg);
        if (!msgObj) return;
        if (msgObj.type !== 'export') return;
        if (String(msgObj.videofeedId) !== String(feedId)) return;
        const dataObj = parseSocketData(msgObj.data);
        const { startRequestId: eventStartRequestId, transcodeBatchUuid: eventTranscodeBatchUuid } = getSocketCorrelation(msgObj, dataObj);
        const currentStartRequestId = String(startRequestIdRef.current || '').trim();
        const currentTranscodeBatchUuid = String(transcodeBatchUuidRef.current || '').trim();
        if (eventStartRequestId || eventTranscodeBatchUuid) {
          const startMatches = !!eventStartRequestId && !!currentStartRequestId && eventStartRequestId === currentStartRequestId;
          const batchMatches =
            !!eventTranscodeBatchUuid && !!currentTranscodeBatchUuid && eventTranscodeBatchUuid === currentTranscodeBatchUuid;
          if (!startMatches && !batchMatches) return;
        } else if (currentStartRequestId || currentTranscodeBatchUuid) {
          return;
        }
        if (typeof msgObj.message !== 'string' || !msgObj.message.trim()) return;
        setExportMessage(msgObj.message);
      } catch {}
    };

    socket.on('export', handler);

    return () => {
      if (socket && (socket as any).off) {
        socket.off('export', handler);
      }
    };
  }, [socket, isOpen, feedId]);

  // === ZIPPING SOCKET LISTENER ===
  useEffect(() => {
    if (!socket || !isOpen || !isZipMode) return;

    const handler = (msg: string) => {
      try {
        const msgObj = JSON.parse(msg);

        if (!msgObj) return;
        if (msgObj.type !== 'zipping' || msgObj.event !== 'zipping') return;
        if (String(msgObj.videofeedId) !== String(feedId)) return;
        if (zipCompletionLockedRef.current) return;

        const dataObj = parseSocketData(msgObj.data);
        const { transcodeBatchUuid: eventTranscodeBatchUuid } = getSocketCorrelation(msgObj, dataObj);
        const currentTranscodeBatchUuid = String(transcodeBatchUuidRef.current || '').trim();
        if (!eventTranscodeBatchUuid || !currentTranscodeBatchUuid || eventTranscodeBatchUuid !== currentTranscodeBatchUuid) return;

        const filesDoneValue = Number(dataObj?.filesDone);
        const totalFilesValue = Number(dataObj?.totalFiles);
        const hasTotalFiles = Number.isFinite(totalFilesValue) && totalFilesValue > 0;
        const hasFilesDone = Number.isFinite(filesDoneValue) && filesDoneValue >= 0;

        if (hasTotalFiles) {
          const roundedTotal = Math.round(totalFilesValue);
          zipFilesTotalRef.current = roundedTotal;
          setZipFilesTotal(roundedTotal);
        }
        if (hasFilesDone) {
          setZipFilesDone(Math.max(0, Math.round(filesDoneValue)));
        }

        let percent: number | null = null;
        if (typeof dataObj?.percent === 'number') {
          percent = dataObj.percent;
        }

        if (percent === null && typeof msgObj.message === 'string') {
          const match = msgObj.message.match(/(\d+(?:\.\d+)?)%/);
          if (match) {
            percent = Number(match[1]);
          }
        } else if (percent === null && typeof msgObj.percent === 'number') {
          percent = msgObj.percent;
        }

        if ((percent === null || !Number.isFinite(percent)) && hasTotalFiles && hasFilesDone) {
          percent = (Math.max(0, Math.min(totalFilesValue, filesDoneValue)) / totalFilesValue) * 100;
        }
        if (percent === null || !Number.isFinite(percent)) return;

        if (percent >= 100) {
          // Socket 100 only means server finished sending; keep UI below 100
          // until browser download trigger is executed in downloadClipsZip.
          setZipFinalizing(true);
          setZipProgress(98);
          return;
        }

        if (percent >= 99) {
          setZipFinalizing(true);
          // keep below 100 until frontend fully received the blob
          setZipProgress(98);
          return;
        }

        setZipFinalizing(false);
        const clamped = Math.max(0, Math.min(98, percent));
        setZipProgress(clamped);
      } catch (e) {
        console.error('Failed to parse zipping socket message', e);
      }
    };

    socket.on('zipping', handler);

    return () => {
      if (socket && (socket as any).off) {
        socket.off('zipping', handler);
      }
      setZipProgress(null);
      setZipFilesDone(null);
      setZipFilesTotal(null);
      zipCompletionLockedRef.current = false;
    };
  }, [socket, isOpen, isZipMode, feedId]);

  const effectivePercent = (() => {
    if (isZipMode && zipProgress !== null && !Number.isNaN(zipProgress)) {
      return zipProgress;
    }

    if (downloading && dlTotal > 0) {
      return Math.round((dlIndex / dlTotal) * 100);
    }

    return Math.round(Number(manifest?.overallProgress ?? 0));
  })();

  const zipEstimateText = (() => {
    if (!isZipMode) return '';

    if (estimatedZipBytesLoading) {
      return 'Estimated ZIP size: calculating...';
    }

    if (Number.isFinite(estimatedZipBytes) && Number(estimatedZipBytes) > 0) {
      return `Estimated ZIP size: ~${formatBytes(Number(estimatedZipBytes))}`;
    }

    if (manifest?.status === 'ready') {
      return 'Estimated ZIP size: unavailable';
    }

    return 'Estimated ZIP size: available when export is ready';
  })();

  const progressSummary = (() => {
    const percent = effectivePercent;
    if (exportError) {
      return exportError;
    }
    const hasZipFileCounts = isZipMode && Number.isFinite(zipFilesTotal) && Number(zipFilesTotal) > 0;
    const zipTotal = hasZipFileCounts ? Number(zipFilesTotal) : 0;
    const zipDone = hasZipFileCounts ? Math.max(0, Math.min(zipTotal, Math.round(zipFilesDone || 0))) : 0;
    const zipWorkActive = isZipMode && ((zipProgress !== null && zipProgress < 100) || downloading || zipFinalizing);

    if (zipWorkActive && hasZipFileCounts) {
      return `Zipping clips • ${zipDone} of ${zipTotal} clips`;
    }

    if (isZipMode && zipProgress !== null && zipProgress < 100) {
      return 'Zipping clips...';
    }

    if (downloading) {
      if (dlTotal > 0) {
        return `Downloading ${dlIndex}/${dlTotal} • ${percent}%`;
      }
      return 'Downloading…';
    }

    if (exportMessage && (!manifest || manifest.status === 'queued')) {
      return exportMessage;
    }

    if (manifest?.counts) {
      return `Completed ${manifest.counts.completed ? `${manifest.counts.completed}/${manifest.counts.total}` : ''} • ${percent}%`;
    }

    if (manifest?.status) {
      return `${manifest.status} • ${percent}%`;
    }

    return 'Preparing export…';
  })();

  const miniDotColor = (() => {
    const status = manifest?.status;

    if (status === 'error' || exportError) {
      return '#d33030';
    }

    // everything done: no loaders, no zipping, manifest ready / 100%
    const allFinished =
      !loading &&
      !polling &&
      !downloading &&
      (!isZipMode || zipProgress === null || zipProgress >= 100) &&
      (manifest?.status === 'ready' || effectivePercent >= 100);

    if (allFinished) {
      // green ONLY when everything is done
      return '#4caf50';
    }

    // yellow while processing, polling, zipping or downloading
    if (
      status === 'processing' ||
      (polling && effectivePercent > 0 && effectivePercent < 100) ||
      (isZipMode && zipProgress !== null && zipProgress < 100) ||
      downloading
    ) {
      return '#ffa000';
    }

    // grey for idle/queued
    return '#9e9e9e';
  })();

  // "Done" state: everything finished, no loaders, 100%
  const isDone =
    !exportError &&
    !loading &&
    !polling &&
    !downloading &&
    (!isZipMode || zipProgress === null || zipProgress >= 100) &&
    (manifest?.status === 'ready' || effectivePercent >= 100);
  const canCancelBeforeTranscoding =
    started &&
    loading &&
    !polling &&
    !downloading &&
    !!startRequestAbortControllerRef.current;
  const exportErrorTitle =
    exportError && /download limit reached|hours limit|period ended/i.test(exportError) ? 'Export blocked' : 'Download failed';
  const reuseLabel = mode === 'angles' || mode === 'anglesPaddingJson' ? 'angle(s)' : 'clip(s)';

  return (
    <>
      <Modal open={reuseConfirmOpen} loading={confirmLoading}>
        <div className={styles.loaderShell}>
          <div className={styles.modalBody}>
            <div className={styles.modalTitle}>
              <div className={styles.modalTop}>
                <div className={styles.titleText}>Reuse pre-generated outputs</div>
              </div>
            </div>
            <div className={styles.form}>
              <div className={styles.actionBtns}>
                <div className={styles.successText}>
                  This will reuse {estimate?.reusedOutputs || estimate?.totalOutputs || 0} {reuseLabel} because they are already generated.
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    marginTop: 16,
                    justifyContent: 'center',
                  }}
                >
                  <ContainedButton
                    className={styles.containedButton}
                    disabled={confirmLoading}
                    onClick={async () => {
                      setReuseConfirmOpen(false);
                      await start();
                    }}
                  >
                    Continue
                  </ContainedButton>
                  <OutlinedButton className={styles.outlinedButton} onClick={() => setReuseConfirmOpen(false)}>
                    Cancel
                  </OutlinedButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal open={confirmOpen} loading={confirmLoading}>
        <div className={styles.loaderShell}>
          <div className={styles.modalBody}>
            <div className={styles.modalTitle}>
              <div className={styles.modalTop}>
                <div className={styles.titleText}>Confirm download</div>
              </div>
            </div>
            <div className={styles.form}>
              <div className={styles.actionBtns}>
                <div className={styles.warningText}>
                  Are you sure you want to generate this content?
                  <br />
                  It will cost: <br />
                  <span style={{ fontWeight: 800, fontStyle: 'italic', fontSize: '18px' }}>
                    {' '}
                    {formatDurationFromHours(estimate?.hours)}.
                  </span>
                </div>
                {estimate?.reusedOutputs ? (
                  <div className={cx(styles.progressMeta, styles.successText)}>
                    {estimate.reusedOutputs} {reuseLabel} already generated will be reused.
                  </div>
                ) : null}
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    marginTop: 16,
                    justifyContent: 'center',
                  }}
                >
                  <ContainedButton
                    className={styles.containedButton}
                    disabled={confirmLoading}
                    onClick={async () => {
                      setConfirmOpen(false);
                      await start();
                    }}
                  >
                    <span>Confirm</span>
                  </ContainedButton>
                  <OutlinedButton
                    className={styles.outlinedButton}
                    disabled={confirmLoading}
                    kind="default"
                    onClick={() => setConfirmOpen(false)}
                  >
                    <span>Cancel</span>
                  </OutlinedButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        open={isOpen && isVisible}
        onClose={
          !loading && !polling && !downloading
            ? () => {
                setHoursLimitMsg(null);
                onClose();
              }
            : () => {}
        }
        loading={loading || polling || downloading}
      >
        <div className={styles.loaderShell}>
          <div
            className={cx(styles.modalBody, styles.downloadModalBody)}
            onClickCapture={(e) => {
              if (!hoursLimitMsg) return;
              const target = e.target as HTMLElement | null;
              if (!target) return;
              const hit = target.closest('button,input,select,textarea,[role="button"],[role="switch"]');
              if (hit) setHoursLimitMsg(null);
            }}
          >
            {(tabs.length > 1 || canAddLayer) && (
              <div className={styles.downloadTabs} role="tablist">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={tab.id === jobId}
                    className={cx(styles.downloadTab, tab.id === jobId && styles.downloadTabActive)}
                    onClick={() => tab.id !== jobId && onSelectTab(tab.id)}
                  >
                    <span>{getDownloadTypeLabel(tab.downloadType)}</span>
                  </button>
                ))}
                {canAddLayer && (
                  <Tooltip title="Add another download type">
                    <button type="button" className={styles.downloadTabAdd} onClick={onAddLayer}>
                      <AddIcon fontSize="small" />
                    </button>
                  </Tooltip>
                )}
              </div>
            )}
            <div className={styles.modalTitle}>
              <div className={styles.modalTop}>
                <div className={styles.titleText}>
                  {!started ? 'Which type of data would you like to download?' : 'Online Composer Download'}
                </div>

                <div className={styles.modalTopButtons}>
                  <Minimize
                    className={styles.iconButtonMinimize}
                    onClick={(e) => {
                      e.stopPropagation();
                      onMinimizeGroup();
                    }}
                  />

                  <Close
                    className={cx(styles.iconButtonClose, loading || polling || downloading ? styles.disabled : '')}
                    onClick={() => {
                      if (loading || polling || downloading) return;
                      setHoursLimitMsg(null);
                      onClose();
                    }}
                  />
                </div>
              </div>
            </div>

            {hoursLimitMsg ? <div className={styles.warningText}>{hoursLimitMsg}</div> : null}
            {(mode === 'clips' || mode === 'selected') && <div className={styles.angleHelper}>{zipEstimateText}</div>}

            <div className={styles.form}>
              <div className={styles.actionBtns}>
                {/* OPTIONS BLOCK — HIDDEN AFTER START */}
                {!started && (
                  <div className={styles.optionsGrid}>
                    {(standaloneReady.angles.isReady || standaloneReady.anglesPaddingJson.isReady) && (
                      <div className={styles.readyLegend}>
                        Green "Ready to download" means pre-generated movie exists. Click it to download immediately.
                      </div>
                    )}

                    {shouldShowAngleSelector && (
                      <div className={styles.clipsSelector}>
                        <ContainedButton
                          onClick={() => setSelectAllAngles(true)}
                          className={cx(styles.selector1, styles.selectorButton, selectAllAngles ? styles.selectorButtonActive : '')}
                        >
                          Download All Angles
                        </ContainedButton>
                        <ContainedButton
                          onClick={() => setSelectAllAngles(false)}
                          className={cx(styles.selector2, styles.selectorButton, !selectAllAngles ? styles.selectorButtonActive : '')}
                        >
                          Download Current Angle
                        </ContainedButton>
                      </div>
                    )}
                    {shouldShowAngleSelector && mode !== 'clips' && <div className={styles.angleHelper}>Exports MP4s per angle</div>}

                    <div className={styles.optionListCard}>
                      {/* Standalone Movie */}
                      <div
                        className={cx(styles.optionRow, mode === 'angles' && styles.active, isModeBlocked('angles') && styles.disabled)}
                        onClick={() => selectMode('angles')}
                        role="checkbox"
                        aria-checked={mode === 'angles'}
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && selectMode('angles')}
                      >
                        <input
                          className={styles.inlineCheckbox}
                          type="checkbox"
                          checked={mode === 'angles'}
                          onChange={() => selectMode('angles')}
                          disabled={loading || polling || downloading || isModeBlocked('angles')}
                        />
                        <div className={styles.optionText}>
                          <div className={styles.optionTitleRow}>
                            <div className={styles.optionTitle}>Download Standalone Movie</div>
                            {standaloneReady.angles.isReady && (
                              <Tooltip title={`Download ${standaloneReady.angles.readyOutputs} ready made standalone movie(s)`}>
                                <button
                                  type="button"
                                  className={styles.readyIconButton}
                                  disabled={loading || polling || downloading || confirmLoading || isModeBlocked('angles')}
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    await startReadyStandaloneDownload('angles');
                                  }}
                                >
                                  <CheckCircleIcon className={styles.readyIcon} />
                                  <span className={styles.readyIconText}>Ready to download</span>
                                </button>
                              </Tooltip>
                            )}
                          </div>
                          <div className={styles.optionSub}>
                            Exports each angle as a separate MP4. Will download automatically when ready.
                          </div>
                        </div>
                      </div>

                      {/* Standalone Movie with Padding & JSON */}
                      <div
                        className={cx(
                          styles.optionRow,
                          mode === 'anglesPaddingJson' && styles.active,
                          isModeBlocked('anglesPaddingJson') && styles.disabled,
                        )}
                        onClick={() => selectMode('anglesPaddingJson')}
                        role="checkbox"
                        aria-checked={mode === 'anglesPaddingJson'}
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && selectMode('anglesPaddingJson')}
                      >
                        <input
                          className={styles.inlineCheckbox}
                          type="checkbox"
                          checked={mode === 'anglesPaddingJson'}
                          onChange={() => selectMode('anglesPaddingJson')}
                          disabled={loading || polling || downloading || isModeBlocked('anglesPaddingJson')}
                        />
                        <div className={styles.optionText}>
                          <div className={styles.optionTitleRow}>
                            <div className={styles.optionTitle}>Download Standalone Movie with Padding &amp; JSON</div>
                            {standaloneReady.anglesPaddingJson.isReady && (
                              <Tooltip
                                title={`Download ${standaloneReady.anglesPaddingJson.readyOutputs} ready made standalone movie(s) with padding`}
                              >
                                <button
                                  type="button"
                                  className={styles.readyIconButton}
                                  disabled={loading || polling || downloading || confirmLoading || isModeBlocked('anglesPaddingJson')}
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    await startReadyStandaloneDownload('anglesPaddingJson');
                                  }}
                                >
                                  <CheckCircleIcon className={styles.readyIcon} />
                                  <span className={styles.readyIconText}>Ready to download</span>
                                </button>
                              </Tooltip>
                            )}
                          </div>
                          <div className={styles.optionSub}>
                            Will export each angle as a seperate MP4 of equal length by padding out the clips containing missing angles with
                            blank footage. Also downloads a Timeline JSON for import into Angles.
                          </div>
                        </div>
                      </div>

                      {/* Individual Clips */}
                      <div
                        className={cx(styles.optionRow, mode === 'clips' && styles.active, isModeBlocked('clips') && styles.disabled)}
                        onClick={() => selectMode('clips')}
                        role="checkbox"
                        aria-checked={mode === 'clips'}
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && selectMode('clips')}
                      >
                        <input
                          className={styles.inlineCheckbox}
                          type="checkbox"
                          checked={mode === 'clips'}
                          onChange={() => selectMode('clips')}
                          disabled={loading || polling || downloading || isModeBlocked('clips')}
                        />
                        <div className={styles.optionText}>
                          <div className={styles.optionTitle}>Download Individual Clips</div>
                          <div className={styles.optionSub}>
                            Exports every clip as an individual MP4 and then downloads them together as a single ZIP file.
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* NEW: Format for Angles Import (nested style) */}
                    {mode === 'clips' && (
                      <div className={styles.subRow}>
                        <label className={styles.inlineLabel}>
                          <input
                            type="checkbox"
                            checked={formatForAnglesImport}
                            onChange={(e) => setFormatForAnglesImport(e.target.checked)}
                            disabled={loading || polling || downloading}
                          />
                          <span>Format for Angles Import</span>
                        </label>
                      </div>
                    )}

                    {/* Clips selector (All / Selected) ONLY when Individual Clips is chosen */}
                    {mode === 'clips' && feedIdUrl?.length && hasMoreThanOneClip && (
                      <div className={styles.clipsSelector}>
                        <ContainedButton
                          onClick={() => setSelectAllClips(true)}
                          className={cx(styles.selector1, styles.selectorButton, selectAllClips ? styles.selectorButtonActive : '')}
                        >
                          Download All Clips
                        </ContainedButton>
                        <ContainedButton
                          onClick={() => setSelectAllClips(false)}
                          className={cx(styles.selector2, styles.selectorButton, !selectAllClips ? styles.selectorButtonActive : '')}
                        >
                          Download Selected Clips
                        </ContainedButton>
                      </div>
                    )}
                  </div>
                )}

                {/* Buttons: Start+Close until done, then only Done */}
                {!isDone ? (
                  <div
                    style={{
                      display: 'flex',
                      gap: 12,
                      marginTop: 16,
                      justifyContent: 'center',
                    }}
                  >
                    <ContainedButton
                      className={styles.containedButton}
                      disabled={
                        loading ||
                        polling ||
                        downloading ||
                        confirmLoading ||
                        isModeBlocked(mode) ||
                        (mode === 'clips' && !selectAllClips && !hasSelection)
                      }
                      onClick={confirmStart}
                    >
                      <span>Download</span>
                    </ContainedButton>

                    {started && (
                      <OutlinedButton
                        className={styles.outlinedButton}
                        disabled={!canCancelBeforeTranscoding}
                        kind="default"
                        onClick={handleCancelBeforeTranscoding}
                      >
                        <span>Cancel</span>
                      </OutlinedButton>
                    )}

                    <OutlinedButton
                      className={styles.outlinedButton}
                      disabled={loading || polling || downloading}
                      kind="default"
                      onClick={onClose}
                    >
                      <span>Close</span>
                    </OutlinedButton>
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      gap: 12,
                      marginTop: 16,
                      justifyContent: 'center',
                    }}
                  >
                    <ContainedButton className={styles.containedButton} onClick={onClose}>
                      <span>Done</span>
                    </ContainedButton>
                  </div>
                )}

                {exportError && (
                  <div className={styles.progressCard}>
                    <div className={styles.progressHeader}>
                      <span
                        className={styles.dot}
                        data-state="error"
                        style={{ background: exportErrorTitle === 'Download failed' ? '#d33030' : '#e08b00' }}
                      />
                      <strong className={styles.progressTitle}>{exportErrorTitle}</strong>
                    </div>
                    <div className={styles.progressBody}>
                      <div className={styles.progressMeta}>{exportError}</div>
                    </div>
                  </div>
                )}

                {!exportError && (polling || downloading || !!manifest?.files?.length || (isZipMode && zipProgress !== null) || started) && (
                  <div className={styles.progressCard}>
                    <div className={styles.progressHeader}>
                      <span
                        className={styles.dot}
                        data-state={downloading ? 'downloading' : manifest?.status || 'queued'}
                        style={{ background: miniDotColor }}
                      />
                      <strong className={styles.progressTitle}>Export status</strong>
                      <span className={styles.progressState}>
                        {isZipMode && zipProgress !== null && zipProgress < 100
                          ? 'zipping'
                          : downloading
                          ? 'downloading'
                          : manifest?.status || 'queued'}
                      </span>
                    </div>
                    <div className={styles.progressBody}>
                      <LinearProgress value={Math.max(0, Math.min(100, Number(effectivePercent)))} variant="determinate" />
                      <div className={styles.progressMeta}>{progressSummary}</div>
                      {downloading && dlTotal > 0 && (
                        <div className={styles.progressMeta}>
                          Downloading files: {dlIndex}/{dlTotal}
                        </div>
                      )}
                    </div>
                    {(!!manifest?.files?.length || started) && (
                      <div className={styles.fileList}>
                        {manifest?.files?.map((f, i) => (
                          <div key={i} className={styles.fileRow}>
                            <span className={styles.fileKey}>{getDisplayFilename(f.key) || f.key}</span>
                            <span className={styles.fileStatus}>{f.ready ? '✓' : ''}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {manifest?.counts && (
                      <div style={{ fontSize: 12, marginTop: 4, marginLeft: 12, opacity: 0.6 }}>
                        {manifest.counts.completed
                          ? `${manifest.counts.completed}/${manifest.counts.total} files`
                          : `${manifest.counts.total} files`}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {isOpen && showMinimized && (
        <Tooltip
          title={
            <div style={{ maxWidth: 260 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{feedTitle}</div>
              <div style={{ fontSize: 12, opacity: 0.8 }}>{progressSummary}</div>
              {manifest?.counts && (
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  {manifest.counts.completed
                    ? `${manifest.counts.completed}/${manifest.counts.total} files`
                    : `${manifest.counts.total} files`}
                </div>
              )}
            </div>
          }
          arrow
          placement="top"
        >
          <div
            onMouseDown={handleMiniMouseDown}
            style={{
              left: miniPos.x,
              top: miniPos.y,
            }}
            className={styles.minimizedModal}
          >
            <div className={styles.minimizedHeader}>
              <span className={styles.minimizedDot} style={{ background: miniDotColor }} />
              <span className={styles.title}>{feedTitle}</span>
              <div className={styles.minimizedProgress}>{progressSummary}</div>
              <MaximizeIcon
                style={{ cursor: 'pointer', marginLeft: '8px' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onRestoreGroup();
                }}
              />
            </div>
            {tabs.length > 1 && (
              <div className={styles.minimizedFloors}>
                {tabs.map((tab) => (
                  <div key={tab.id} className={cx(styles.minimizedFloor, tab.id === jobId && styles.minimizedFloorActive)}>
                    {getDownloadTypeLabel(tab.downloadType)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Tooltip>
      )}
    </>
  );
};

export default ComposerDownloadModal;
